import { useMemo } from 'react';
import { BookOpen, Expand, Footprints } from 'lucide-react';
import { CityStatistics } from '../types';

interface CityRankingsProps {
  selectedCity: string;
  cityStats: { [key: string]: { stats: CityStatistics } };
}

interface MetricData {
  city: string;
  value: number;
  isSelected: boolean;
}

const RankingChart = ({ 
  data,
  label,
  icon: Icon,
  suffix = '',
}: {
  data: MetricData[];
  label: string;
  icon: any;
  suffix?: string;
  valueColor?: string;
}) => {
  // Find the maximum value for scaling
  const maxValue = Math.max(...data.map(d => d.value));
  
  return (
    <div className="bg-zinc-800 p-4 rounded-none">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-zinc-400" />
        <h3 className="text-sm text-zinc-400">{label}</h3>
      </div>
      
      <div className="space-y-1">
        {data.map((item) => (
          <div key={item.city} className="flex items-center gap-2">
            <div className="w-36 text-right">
              <span className={`text-[11px] ${item.isSelected ? 'text-zinc-200' : 'text-zinc-400'} whitespace-nowrap overflow-hidden text-ellipsis inline-block max-w-full`} title={item.city}>
                {item.city}
              </span>
            </div>
            <div className="flex-1 h-4 relative">
              <div 
                className={`h-full ${item.isSelected ? 'bg-orange-500' : 'bg-zinc-600'}`}
                style={{ width: `${(item.value / maxValue) * 100}%` }}
              />
              <span className={`absolute left-1 top-1/2 -translate-y-1/2 text-[10px] ${
                item.isSelected ? 'text-zinc-900' : 'text-zinc-300'
              }`}>
                {item.value.toFixed(1)}{suffix}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CityRankings = ({ selectedCity, cityStats }: CityRankingsProps) => {
  const metrics = useMemo(() => {
    const cities = Object.entries(cityStats).map(([city, { stats }]) => ({
      city,
      stats
    }));

    return [
      {
        label: 'Bookshops per 100k',
        data: cities
          .map(({ city, stats }) => ({
            city,
            value: stats.bookstoresPerCapita || 0,
            isSelected: city === selectedCity
          }))
          .sort((a, b) => b.value - a.value),
        icon: BookOpen
      },
      {
        label: 'Independent Stores (%)',
        data: cities
          .map(({ city, stats }) => ({
            city,
            value: stats.independentStats?.percentage || 0,
            isSelected: city === selectedCity
          }))
          .sort((a, b) => b.value - a.value),
        icon: Expand,
        suffix: '%'
      },
      {
        label: 'Walkable Stores (%)',
        data: cities
          .map(({ city, stats }) => ({
            city,
            value: stats.geoStats?.walkablePercentage || 0,
            isSelected: city === selectedCity
          }))
          .sort((a, b) => b.value - a.value),
        icon: Footprints,
        suffix: '%'
      }
    ];
  }, [cityStats, selectedCity]);

  return (
    <div className="mb-16">
      <h2 className="text-sm text-zinc-400 mb-4 flex items-center gap-2">
        <span className="w-4 h-4">📊</span>
        City Rankings
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {metrics.map((metric) => (
          <RankingChart
            key={metric.label}
            label={metric.label}
            data={metric.data}
            icon={metric.icon}
            suffix={metric.suffix}
          />
        ))}
      </div>
    </div>
  );
};

export default CityRankings;